import { Controller, useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import FormField from "@/components/ui/formField";
import useWidgetConfiguration from "@/hooks/dataset-widget/useWidgetConfiguration";
import { WidgetType } from "@/types/widget";

import { WidgetFormData } from ".";

type PlaceholderMap = {
  dimension: string;
  measure: string;
  dimension_values_shown: string;
  group_by: string;
};

const placeHolderMap: Record<WidgetType, PlaceholderMap> = {
  [WidgetType.BAR_H]: {
    dimension: "Dimension (Y axis)",
    measure: "Measure (X axis)",
    dimension_values_shown: "Bar Shown",
    group_by: "Group By"
  },
  [WidgetType.BAR_V]: {
    dimension: "Dimension (Y axis)",
    measure: "Measure (X axis)",
    dimension_values_shown: "Bar Shown",
    group_by: "Group By"
  },
  [WidgetType.DONUT]: {
    dimension: "Dimension (Slice)",
    measure: "Value",
    dimension_values_shown: "No of Slice",
    group_by: "Order By"
  },
  [WidgetType.LINE]: {
    dimension: "Dimension",
    measure: "Value",
    dimension_values_shown: "",
    group_by: ""
  },
  [WidgetType.BIG_NUMBER]: {
    dimension: "",
    measure: "Measure",
    dimension_values_shown: "",
    group_by: ""
  }
};

const FormWidgetConfiguration = ({
  widgetType
}: {
  widgetType: WidgetType;
}) => {
  const { register, control, watch } = useFormContext<WidgetFormData>();

  const watchDatasetId = watch("dataset_id");

  const {
    datasetOptions,
    dimensionOptions,
    measureOptions,
    aggregationOptions,
    orderByOptions,
    dimensionValueShownOptions
  } = useWidgetConfiguration({ datasetId: watchDatasetId, widgetType });

  const orderByDropdown = (
    <Controller
      name="order_by"
      control={control}
      render={({ field: { value, onChange } }) => (
        <Dropdown
          name="order_by"
          value={
            orderByOptions.find((option) => option.value === value) || null
          }
          placeholder="Order by"
          data={orderByOptions}
          onSelect={(val) => {
            onChange(val?.value);
          }}
          disabled={!watchDatasetId}
        />
      )}
    />
  );

  return (
    <div className="flex-shrink-0 flex-grow-0 border border-whisper rounded-lg p-4 h-full overflow-y-auto max-w-[300px]">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold text-txtHeading">
            Configuration
          </h1>
          <p className="text-xs text-txtBody">
            Select a data source to generate visual insights from dataset
          </p>

          <FormField
            register={register}
            name="label"
            placeHolder="Label"
            rules={{ required: true }}
          />
          <div>
            <Controller
              name="dataset_id"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  value={
                    datasetOptions.find((option) => option.value === value) ||
                    null
                  }
                  placeholder="Data source"
                  textTransform="capitalize-words"
                  data={datasetOptions}
                  onSelect={(val) => {
                    onChange(val?.value);
                  }}
                />
              )}
            />
            <span className="text-[10px] text-txtBody px-2">
              Select available data source
            </span>
          </div>

          {/* Data Configuration */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold text-txtPlaceholder">
              Data Configuration
            </p>
            {widgetType !== WidgetType.BIG_NUMBER && (
              <Controller
                name="dimension"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    name="dimension"
                    value={
                      dimensionOptions.find(
                        (option) => option.value === value
                      ) || null
                    }
                    placeholder={placeHolderMap[widgetType].dimension}
                    data={dimensionOptions}
                    onSelect={(val) => {
                      onChange(val?.value);
                    }}
                    disabled={!watchDatasetId}
                  />
                )}
              />
            )}
            <div>
              <Controller
                name="measure"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    name="measure"
                    value={
                      measureOptions.find((option) => option.value === value) ||
                      null
                    }
                    placeholder={placeHolderMap[widgetType].measure}
                    data={measureOptions}
                    onSelect={(val) => {
                      onChange(val?.value);
                    }}
                    disabled={!watchDatasetId}
                  />
                )}
              />
              {(widgetType === WidgetType.BAR_H ||
                widgetType === WidgetType.BAR_V ||
                widgetType === WidgetType.DONUT) && (
                <span className="text-[10px] text-txtBody px-2">Optional</span>
              )}
            </div>
            <Controller
              name="aggregation"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  name="aggregation"
                  value={
                    aggregationOptions.find(
                      (option) => option.value === value
                    ) || null
                  }
                  placeholder="Aggregation"
                  data={aggregationOptions}
                  onSelect={(val) => {
                    onChange(val?.value);
                  }}
                  disabled={!watchDatasetId}
                />
              )}
            />
            {(widgetType === WidgetType.BAR_H ||
              widgetType === WidgetType.BAR_V) &&
              orderByDropdown}
            {(widgetType === WidgetType.BAR_H ||
              widgetType === WidgetType.BAR_V ||
              widgetType === WidgetType.DONUT) && (
              <Controller
                name="dimension_values_shown"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Dropdown
                    name="dimension_values_shown"
                    value={
                      dimensionValueShownOptions.find(
                        (option) => Number(option.value) === value
                      ) || null
                    }
                    placeholder={
                      placeHolderMap[widgetType].dimension_values_shown
                    }
                    data={dimensionValueShownOptions}
                    onSelect={(val) => {
                      onChange(val ? Number(val.value) : undefined);
                    }}
                    disabled={!watchDatasetId}
                  />
                )}
              />
            )}
            {widgetType === WidgetType.DONUT && orderByDropdown}
          </div>
        </div>
        <div className="flex flex-col">
          <Button variant="outline" className="rounded-xl" onClick={() => {}}>
            Preview
          </Button>
          <Button className="mt-2 rounded-xl" onClick={() => {}}>
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormWidgetConfiguration;
